import { api } from './client'
import { jobsApi } from './jobs'

/**
 * F-117 (TASK-0371, REQ-266-001 / REQ-265-001) — export con approvazione e visualizzazioni tracciate.
 *
 * Con l'obbligo acceso sul server (`Exports:ApprovalRequired:*`) l'export risponde 428 `EXPORT_APPROVAL_REQUIRED`:
 * `downloadExportFile` lo trasforma in `ExportApprovalRequiredError`, che la pagina intercetta per aprire
 * `ExportApprovalDialog`. Una richiesta approvata si scarica ripetendo lo STESSO export con `approvalId`.
 */

export type ExportRequestStatus = 'Pending' | 'Approved' | 'Rejected' | 'Used' | 'Expired'

export interface ExportRequest {
  id: number
  dataset: string
  filters: Record<string, string>
  fingerprint: string
  requestedBy: number
  reason: string
  status: ExportRequestStatus
  decidedBy: number | null
  decidedAt: string | null
  decisionNote: string | null
  expiresAt: string | null
  usedAt: string | null
  createdAt: string
}

export const EXPORT_STATUS_LABELS: Record<ExportRequestStatus, string> = {
  Pending: 'In attesa',
  Approved: 'Approvata',
  Rejected: 'Rifiutata',
  Used: 'Usata',
  Expired: 'Scaduta',
}

/** Il server ha chiesto l'approvazione: dataset e impronta dei filtri per cui serve. */
export class ExportApprovalRequiredError extends Error {
  readonly dataset: string
  readonly fingerprint: string

  constructor(dataset: string, fingerprint: string) {
    super('Questo export richiede l’approvazione di un amministratore.')
    this.name = 'ExportApprovalRequiredError'
    this.dataset = dataset
    this.fingerprint = fingerprint
  }
}

export interface ExportFile {
  blob: Blob
  filename: string
}

/**
 * R3-19: con il flag `job.export_asincroni` un export grande risponde 202 `{ jobId }`: si segue il job
 * (`GET /api/jobs/mine/{id}`, con `onProgress` per l'avanzamento) e, a lavoro concluso, si scarica il file.
 */
export async function waitForQueuedFile(
  jobId: number,
  onProgress?: (done: number, total: number) => void,
  maxWaitMs = 10 * 60_000,
): Promise<ExportFile> {
  const started = Date.now()
  let delay = 1000
  for (;;) {
    const state = await jobsApi.mine(jobId)
    if (state.total !== null && state.done !== null) onProgress?.(state.done, state.total)
    if (state.finished) {
      if (state.status !== 'processed' || !state.file) {
        throw new Error(state.error ?? 'La preparazione del file non è riuscita.')
      }
      const res = await fetch(state.file.url, { credentials: 'include' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return { blob: await res.blob(), filename: state.file.name }
    }
    if (Date.now() - started > maxWaitMs) {
      throw new Error('Il file è ancora in preparazione: lo trovi tra poco, riprova più tardi.')
    }
    await new Promise(resolve => setTimeout(resolve, delay))
    delay = Math.min(5000, Math.round(delay * 1.5))
  }
}

/** Scarica un export CSV; il 428 dell'approvazione diventa `ExportApprovalRequiredError`. */
export async function downloadExportFile(url: string, onProgress?: (done: number, total: number) => void): Promise<ExportFile> {
  const res = await fetch(url, { credentials: 'include' })
  if (res.status === 202) {
    const queued = await res.json().catch(() => null) as { jobId?: number } | null
    if (queued?.jobId) return waitForQueuedFile(queued.jobId, onProgress)
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as
      { error?: string; title?: string; code?: string; dataset?: string; fingerprint?: string }
    if (res.status === 428 && err.code === 'EXPORT_APPROVAL_REQUIRED') {
      throw new ExportApprovalRequiredError(err.dataset ?? '', err.fingerprint ?? '')
    }
    throw new Error(err.error ?? err.title ?? `HTTP ${res.status}`)
  }
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
  return { blob: await res.blob(), filename: match?.[1] ? decodeURIComponent(match[1]) : 'export.csv' }
}

/** Fa scaricare il file al browser. */
export function saveExportFile({ blob, filename }: ExportFile): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** L'indirizzo dell'export di un dataset (`contracts` oppure `finance:{dataset}`) con i filtri e, se c'è, l'approvazione. */
export function exportUrl(dataset: string, filters: Record<string, string>, approvalId?: number): string {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(filters)) if (v) q.set(k, v)
  if (approvalId !== undefined) q.set('approvalId', String(approvalId))
  const qs = q.toString()
  const base = dataset === 'contracts'
    ? '/api/contracts/export'
    : `/api/finance/exports/${dataset.replace(/^finance:/, '')}`
  return qs ? `${base}?${qs}` : base
}

/** F-140 / C-14 (REQ-026-004): lo stesso export in Excel (`format=xlsx`) o PDF (`format=pdf`); il server converte il CSV gia' prodotto. */
export function withFormat(url: string, format: 'csv' | 'xlsx' | 'pdf'): string {
  if (format === 'csv') return url
  return `${url}${url.includes('?') ? '&' : '?'}format=${format}`
}

export const exportsApi = {
  create: (body: { dataset: string; filters: Record<string, string>; reason: string }) =>
    api<ExportRequest>('/api/export-requests', { method: 'POST', body: JSON.stringify(body) }),

  mine: () => api<{ items: ExportRequest[] }>('/api/export-requests/mine'),

  /** Solo Admin (403 per gli altri). */
  pending: () => api<{ items: ExportRequest[] }>('/api/export-requests/pending'),

  approve: (id: number, note?: string) =>
    api<ExportRequest>(`/api/export-requests/${id}/approve`, { method: 'POST', body: JSON.stringify({ note: note ?? null }) }),

  reject: (id: number, note?: string) =>
    api<ExportRequest>(`/api/export-requests/${id}/reject`, { method: 'POST', body: JSON.stringify({ note: note ?? null }) }),

  /** Scarica una richiesta approvata. */
  download: async (request: ExportRequest) =>
    saveExportFile(await downloadExportFile(exportUrl(request.dataset, request.filters, request.id))),

  /** Export delle pratiche con i filtri correnti (`approvalId` dopo l'approvazione). */
  downloadContracts: async (filters: Record<string, string>, approvalId?: number) =>
    saveExportFile(await downloadExportFile(exportUrl('contracts', filters, approvalId))),

  /** REQ-265-001: dichiara stampa o condivisione di una pratica o di un contatto (il server scrive l'audit). */
  accessEvent: (action: 'print' | 'share', entityType: 'Contract' | 'Contact', entityId: number) =>
    api<void>('/api/audit/access-events', { method: 'POST', body: JSON.stringify({ action, entityType, entityId }) }),
}
